/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import {
  FiShuffle,
  FiUpload,
  FiPlus,
  FiChevronDown,
  FiEye,
  FiList,
} from "react-icons/fi";
import { ReactFlowProvider } from "@xyflow/react";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { Title } from "baseui/card/styled-components";
import { BiEdit } from "react-icons/bi";
import { OverflowMenu } from "../Shared/OverflowMenu";
import { RiFileUploadLine } from "react-icons/ri";
import { TbArrowsShuffle, TbFileExport, TbSitemap } from "react-icons/tb";
import { TbFilter } from "react-icons/tb";
import { TransformEngine } from "../TransformEngine/TransformEngine";
import EmptyPageComponent, {
  EmptyStateButton,
} from "../Shared/PageComponents/EmptyPageComponent";

type MappingViewMode = "list" | "canvas";

type MappingsViewState = {
  createMappingOpen: boolean;
  viewMode: MappingViewMode;
};

// Snapshot of this page's view state, kept outside the component so it
// survives MappingsIndex being unmounted and remounted
let persistedMappingsViewState: MappingsViewState = {
  createMappingOpen: false,
  viewMode: "list",
};

function usePersistedViewState<K extends keyof MappingsViewState>(
  key: K
): [
  MappingsViewState[K],
  React.Dispatch<React.SetStateAction<MappingsViewState[K]>>
] {
  const [value, setValue] = React.useState<MappingsViewState[K]>(
    persistedMappingsViewState[key]
  );

  const setPersistedValue = React.useCallback<
    React.Dispatch<React.SetStateAction<MappingsViewState[K]>>
  >(
    (update) => {
      setValue((prev) => {
        const next =
          typeof update === "function"
            ? (update as (prev: MappingsViewState[K]) => MappingsViewState[K])(
                prev
              )
            : update;
        persistedMappingsViewState = {
          ...persistedMappingsViewState,
          [key]: next,
        };
        return next;
      });
    },
    [key]
  );

  return [value, setPersistedValue];
}

type MappingsIndexProps = {
  // Lifted from App, same as TopHeader/StandardSideNav, so TransformEngine
  // (rendered a level deeper here) can still hide/show all three together.
  isFocusMode: boolean;
  setIsFocusMode: React.Dispatch<React.SetStateAction<boolean>>;
};

export function MappingsIndex({
  isFocusMode,
  setIsFocusMode,
}: MappingsIndexProps): JSX.Element {
  const { currentTheme } = useDexterTheme();

  const [createMappingOpen, setCreateMappingOpen] =
    usePersistedViewState("createMappingOpen");

  const [viewMode, setViewMode] = usePersistedViewState("viewMode");

  const viewToggleButtonStyle = (
    isActive: boolean,
    compact: boolean = false
  ): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: compact ? "4px" : "6px",
    background: isActive
      ? currentTheme.colors.buttonPrimaryColor
      : currentTheme.colors.pageBackgroundColor,
    color: isActive
      ? currentTheme.colors.primaryElementFontColor
      : currentTheme.colors.text,
    border: "none",
    borderRadius: "6px",
    padding: compact ? "3px 10px" : "6px 14px",
    fontFamily: currentTheme.fonts.primary,
    fontWeight: 600,
    fontSize: compact ? "0.72rem" : "0.85rem",
    lineHeight: 1,
    cursor: "pointer",
  });

  // Reusable List/Canvas toggle. `compact` is used when this renders inline
  // inside TransformEngine's own toolbar
  const viewToggleGroup = (compact: boolean): JSX.Element => (
    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
      <button
        onClick={() => setViewMode("list")}
        style={viewToggleButtonStyle(viewMode === "list", compact)}
      >
        <FiList
          size={
            compact
              ? currentTheme.iconSizes.compact
              : currentTheme.iconSizes.default
          }
        />
        List
      </button>

      <button
        data-testId="canvas-view-button"
        onClick={() => setViewMode("canvas")}
        style={viewToggleButtonStyle(viewMode === "canvas", compact)}
      >
        <TbSitemap
          size={
            compact
              ? currentTheme.iconSizes.compact
              : currentTheme.iconSizes.default
          }
        />
        Canvas
      </button>
    </div>
  );

  // In canvas view, the top header (title + Preview/Export/overflow) is
  // hidden entirely — only the List/Canvas toggle survives, relocated inline
  // with TransformEngine's own toolbar.
  const showTopHeader = !(createMappingOpen && viewMode === "canvas");

  return (
    <>
      {showTopHeader && (
        <div
          style={{
            borderBottom: `1px solid ${currentTheme.colors.avatarBackgroundColor}`,
            backgroundColor: currentTheme.colors.pageBackgroundColor,
            padding: "5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          <Title
            style={{
              display: "flex",
              alignItems: "center",
              fontFamily: currentTheme.fonts.primary,
              fontSize: "20px",
              lineHeight: "24px",
              fontWeight: 600,
              marginLeft: "15px",
              marginTop: "10px",
              marginBottom: 0,
            }}
          >
            {createMappingOpen ? (
              // Placeholder title for now
              <div>
                Untitled <BiEdit size={15} />
              </div>
            ) : (
              <div>Mappings</div>
            )}
          </Title>

          {createMappingOpen && (
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                borderRadius: "8px",
                padding: "3px",
              }}
            >
              {viewToggleGroup(false)}
            </div>
          )}

          <div
            style={{
              marginRight: "15px",
              display: "flex",
              alignItems: "center",
            }}
          >
            {createMappingOpen && (
              <>
                <button
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: currentTheme.colors.buttonSecondaryColor,
                    color: currentTheme.colors.text,
                    border: "none",
                    borderRadius: "8px",
                    padding: "9px 16px",
                    fontFamily: currentTheme.fonts.primary,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    marginRight: "10px",
                  }}
                >
                  <FiEye size={16} />
                  Preview
                </button>

                <button
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    background: currentTheme.colors.buttonPrimaryColor,
                    color: currentTheme.colors.primaryElementFontColor,
                    border: "none",
                    borderRadius: "8px",
                    padding: "9px 16px",
                    fontFamily: currentTheme.fonts.primary,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    cursor: "pointer",
                  }}
                >
                  <TbFileExport size={16} />
                  Export
                  <FiChevronDown size={14} />
                </button>
              </>
            )}
            <OverflowMenu overflowActions={[]} />
          </div>
        </div>
      )}

      {createMappingOpen ? (
        viewMode === "canvas" ? (
          //Full ReactFlow canvas
          <ReactFlowProvider>
            <TransformEngine
              isFocusing={isFocusMode}
              setIsFocusing={setIsFocusMode}
              isActive={createMappingOpen && viewMode === "canvas"}
              viewToggle={viewToggleGroup(true)}
            />
          </ReactFlowProvider>
        ) : (
          // Mapping editor: input/output panes
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              gap: "16px",
              padding: "16px",
              fontFamily: currentTheme.fonts.primary,
              font: currentTheme.fonts.primary,
              backgroundColor: currentTheme.colors.pageBackgroundColor,
            }}
          >
            {/* Input pane */}
            <div
              style={{
                flex: 1,
                border: `1px solid ${currentTheme.colors.transferListContainerBorderColor}`,
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  color: currentTheme.colors.text,
                  padding: "15px 0 15px 10px",
                  borderBottom: `1px solid ${currentTheme.colors.transferListContainerBorderColor}`,
                }}
              >
                <div
                  style={{
                    backgroundColor: currentTheme.colors.pageBackgroundColor,
                    padding: "5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <span
                      style={{
                        color: currentTheme.colors.defaultTextColor,
                        fontWeight: 600,
                      }}
                    >
                      Input
                    </span>
                    <span
                      style={{ color: currentTheme.colors.secondaryTextColor }}
                    >
                      {" "}
                      (source)
                    </span>
                  </div>
                  <div
                    style={{
                      marginRight: "15px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <TbFilter />
                  </div>
                </div>
              </div>
              {/* input pane content goes here */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "16px",
                  textAlign: "center",
                  fontFamily: currentTheme.fonts.primary,
                  backgroundColor: currentTheme.colors.pageBackgroundColor,
                }}
              >
                <EmptyPageComponent
                  icon={RiFileUploadLine}
                  title="No source yet"
                  description="Upload a file or select from saved sources"
                  actions={
                    <>
                      <EmptyStateButton>Upload file</EmptyStateButton>
                      <EmptyStateButton variant="secondary">
                        Select existing
                      </EmptyStateButton>
                    </>
                  }
                />
              </div>
            </div>

            {/* Output pane */}
            <div
              style={{
                flex: 1,
                border: `1px solid ${currentTheme.colors.transferListContainerBorderColor}`,
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontSize: "0.95rem",
                  fontWeight: 700,
                  color: currentTheme.colors.text,
                  padding: "15px 0 15px 10px",
                  borderBottom: `1px solid ${currentTheme.colors.transferListContainerBorderColor}`,
                }}
              >
                <div
                  style={{
                    backgroundColor: currentTheme.colors.pageBackgroundColor,
                    padding: "5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <span
                      style={{
                        color: currentTheme.colors.defaultTextColor,
                        fontWeight: 600,
                      }}
                    >
                      Output
                    </span>
                    <span
                      style={{ color: currentTheme.colors.secondaryTextColor }}
                    >
                      {" "}
                      (destination)
                    </span>
                  </div>
                  <div
                    style={{
                      marginRight: "15px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <TbFilter />
                  </div>
                </div>
              </div>
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "16px",
                  textAlign: "center",
                  fontFamily: currentTheme.fonts.primary,
                  backgroundColor: currentTheme.colors.pageBackgroundColor,
                }}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: currentTheme.colors.backgroundTertiary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TbArrowsShuffle
                    size={22}
                    color={currentTheme.colors.secondaryTextColor}
                  />
                </div>

                <div
                  style={{
                    fontSize: "1.05rem",
                    fontWeight: 700,
                    font: currentTheme.fonts.primary,
                    color: currentTheme.colors.text,
                  }}
                >
                  No fields yet
                </div>

                <div
                  style={{
                    fontSize: "0.9rem",
                    color: currentTheme.colors.secondaryTextColor,
                    maxWidth: "360px",
                    lineHeight: 1.5,
                  }}
                >
                  Transfer fields from input source(s)
                </div>
              </div>
            </div>
          </div>
        )
      ) : (
        // Create new mapping button not clicked. Initial state of mappings view
        <EmptyPageComponent
          icon={FiShuffle}
          title="No mappings yet"
          description="Mappings define how data fields from an input source are transformed and routed to an output format."
          actions={
            <>
              <EmptyStateButton
                data-testid="create-mapping-button"
                icon={FiPlus}
                onClick={() => setCreateMappingOpen(true)}
              >
                Create mapping
              </EmptyStateButton>
              <EmptyStateButton variant="secondary" icon={FiUpload}>
                Upload mapping
              </EmptyStateButton>
            </>
          }
        />
      )}
    </>
  );
}
